import {FormEvent, useCallback, useEffect, useRef, useState} from 'react';
import {FeedPost, socialFeedApi} from '../../../../services/socialFeedApi';
import {isRequestCanceled} from '../utils';

const MAX_REPOST_MESSAGE_LENGTH = 5000;

export const useRepostComposer = (
  token: string | null,
  onRepostCreated: (post: FeedPost) => void,
) => {
  const repostRequestControllerRef = useRef<AbortController | null>(null);
  const repostTextareaRef = useRef<HTMLTextAreaElement>(null);

  const [isRepostModalOpen, setIsRepostModalOpen] = useState(false);
  const [repostTargetPost, setRepostTargetPost] = useState<FeedPost | null>(null);
  const [repostMessage, setRepostMessage] = useState('');
  const [repostLoading, setRepostLoading] = useState(false);
  const [repostError, setRepostError] = useState<string | null>(null);
  const [repostValidationError, setRepostValidationError] = useState<string | null>(null);

  const resetComposerState = useCallback(() => {
    setRepostMessage('');
    setRepostLoading(false);
    setRepostError(null);
    setRepostValidationError(null);
  }, []);

  const closeRepostModal = useCallback(() => {
    repostRequestControllerRef.current?.abort();
    repostRequestControllerRef.current = null;
    setIsRepostModalOpen(false);
    setRepostTargetPost(null);
    resetComposerState();
  }, [resetComposerState]);

  const openRepostModal = useCallback((post: FeedPost) => {
    if (post.postType === 'repost') {
      return;
    }

    repostRequestControllerRef.current?.abort();
    repostRequestControllerRef.current = null;
    setRepostTargetPost(post);
    setIsRepostModalOpen(true);
    resetComposerState();
  }, [resetComposerState]);

  const handleRepostMessageChange = useCallback((value: string) => {
    setRepostMessage(value);
    setRepostError(null);
    setRepostValidationError(null);
  }, []);

  const handleRepostSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token) {
      setRepostError('You must be signed in to repost a blog.');
      return;
    }

    if (!repostTargetPost) {
      setRepostError('We could not find the blog you are trying to repost.');
      return;
    }

    if (repostTargetPost.postType === 'repost') {
      setRepostValidationError('Reposted blogs cannot be reposted again.');
      setRepostError(null);
      return;
    }

    const trimmedMessage = repostMessage.trim();

    if (trimmedMessage.length > MAX_REPOST_MESSAGE_LENGTH) {
      setRepostValidationError(`Your repost message must be ${MAX_REPOST_MESSAGE_LENGTH} characters or less.`);
      setRepostError(null);
      return;
    }

    repostRequestControllerRef.current?.abort();
    const controller = new AbortController();
    repostRequestControllerRef.current = controller;
    const requestSignal = controller.signal;

    try {
      setRepostLoading(true);
      setRepostError(null);
      setRepostValidationError(null);

      const response = await socialFeedApi.repostPost(
        token,
        repostTargetPost._id,
        {
          message: trimmedMessage || undefined,
        },
        requestSignal,
      );

      if (requestSignal.aborted) {
        return;
      }

      onRepostCreated(response.post);
      setIsRepostModalOpen(false);
      setRepostTargetPost(null);
      resetComposerState();
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setRepostError(error instanceof Error ? error.message : 'Failed to repost blog');
    } finally {
      if (!requestSignal.aborted) {
        setRepostLoading(false);
      }

      if (repostRequestControllerRef.current?.signal === requestSignal) {
        repostRequestControllerRef.current = null;
      }
    }
  }, [onRepostCreated, repostMessage, repostTargetPost, resetComposerState, token]);

  useEffect(() => {
    if (!isRepostModalOpen) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      repostTextareaRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [isRepostModalOpen]);

  useEffect(() => () => {
    repostRequestControllerRef.current?.abort();
  }, []);

  return {
    isRepostModalOpen,
    repostTargetPost,
    repostMessage,
    repostLoading,
    repostError,
    repostValidationError,
    repostTextareaRef,
    setRepostMessage: handleRepostMessageChange,
    openRepostModal,
    closeRepostModal,
    handleRepostSubmit,
  };
};
